/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Modal } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';

import {
  PageWrapper,
  PageHeader,
  PageTitle,
  PageDescription,
  Card,
  CardHeader,
  CardBody,
  SectionTitle,
  SectionIcon,
  ActionItem,
  ActionName,
  ActionControls,
  IndentedContent,
  CheckboxLabel,
  SearchInput,
  SearchIcon,
  Input,
  NoResults,
  ActionBadge,
  ActionIcon,
  Badge,
  HeaderControls
} from './PermissionCustomComponents';
import { useFormik } from 'formik';
import useFetchData from 'hooks/custom/useFetchData';
import apiEndpoints from 'constants/apiEndpoints';
import LabelSelector from 'components/custom/forms/LabelSelector';
import { NewRoleBtn } from './CreateRoleModal';
import useModalManage from 'hooks/custom/useModalManage';
import { useAlert } from 'providers/AlertModalProvider';
import postData from 'services/postData';
import fetchData from 'services/fetchData';
import PageLoader from 'components/custom/animate-component/PageLoader';
import styled from '@emotion/styled';
import Button from 'components/base/Button';
import useShowModal from 'hooks/custom/useShowModal';
import EditRoleModal from './EditRoleModal';

// The permission structure from the JSON file
const permissionStructure = {
  'Dashboard Section': {
    Title: 'Dashboard',
    Pages: [
      {
        Name: 'Payment Dashboard',
        Actions: {
          View: false
        }
      },
      {
        Name: 'HRMS Dashboard',
        Actions: {
          View: false
        }
      },
      {
        Name: 'FlyAsh Report',
        Actions: {
          View: false
        }
      },
      {
        Name: 'Coal-Iron Ore Report',
        Actions: {
          View: false
        }
      }
    ]
  },
  'Payment Section': {
    Title: 'Payment',
    Pages: [
      {
        Name: 'Quick Payment',
        Actions: {
          View: false,
          Payer: false
        }
      },
      {
        Name: 'Challan',
        'Sub-pages': [
          {
            Name: 'Coal/Iron Ore',
            Actions: {
              View: false,
              Maker: false,
              Edit: false,
              Delete: false,
              Payer: false
            }
          },
          {
            Name: 'Coal/Iron Ore POD',
            Actions: {
              View: false,
              Maker: false,
              Checker: false,
              Approver: false,
              Edit: false
            }
          },
          {
            Name: 'Cancelled Coal/Iron ore Challans',
            Actions: {
              View: false
            }
          },
          {
            Name: 'FlyAsh',
            Actions: {
              View: false,
              Maker: false,
              Payer: false
            }
          },
          {
            Name: 'Cancelled FlyAsh Challans',
            Actions: {
              View: false
            }
          }
        ]
      },
      {
        Name: 'Payment History',
        Actions: {
          View: false
        }
      },
      {
        Name: 'Vendor Invoices',
        Actions: {
          View: false,
          Maker: false,
          Checker: false,
          Approver: false,
          Payer: false
        }
      },
      {
        Name: 'Rejected Invoices',
        Actions: {
          View: false
        }
      },
      {
        Name: 'Trucker Payments',
        Actions: {
          View: false,
          Maker: false,
          Checker: false,
          Approver: false,
          Payer: false
        }
      }
    ]
  },
  'Masters Section': {
    Title: 'Masters',
    Pages: [
      {
        Name: 'Vendors',
        'Sub-pages': [
          {
            Name: 'Regular Vendor',
            Actions: {
              View: false,
              Maker: false,
              Edit: false,
              Delete: false,
              Approver: false
            }
          },
          {
            Name: 'Trucker Vendor',
            Actions: {
              View: false,
              Maker: false,
              Edit: false,
              Delete: false,
              Approver: false
            }
          },
          {
            Name: 'Fuel Retailer',
            Actions: {
              View: false,
              Maker: false,
              Edit: false,
              Delete: false,
              Approver: false
            }
          }
        ]
      },
      {
        Name: 'Vehicle',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      },
      {
        Name: 'Location',
        'Sub-pages': [
          {
            Name: 'Branch',
            Actions: {
              View: false,
              Maker: false,
              Edit: false,
              Delete: false,
              Approver: false
            }
          },
          {
            Name: 'Location',
            Actions: {
              View: false,
              Maker: false,
              Edit: false,
              Delete: false,
              Approver: false
            }
          },
          {
            Name: 'Route',
            Actions: {
              View: false,
              Maker: false,
              Edit: false,
              Delete: false,
              Approver: false
            }
          }
        ]
      },
      {
        Name: 'Customers',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      }
    ]
  },
  'HRMS Section': {
    Title: 'HRMS',
    Pages: [
      {
        Name: 'Employee',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      },
      {
        Name: 'Driver',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      },
      {
        Name: 'Leave',
        Actions: {
          View: false,
          Maker: false,
          Approver: false
        }
      },
      {
        Name: 'Separated Employee',
        Actions: {
          View: false,
          edit: false
        }
      },
      {
        Name: 'Manual Punch',
        Actions: {
          View: false,
          Checker: false,
          Approver: false
        }
      },
      {
        Name: 'Late Punch-in Employee',
        Actions: {
          Approver: false
        }
      },
      {
        Name: 'Casual Break',
        Actions: {
          Approver: false
        }
      },
      {
        Name: 'Outer Working',
        Actions: {
          Approver: false
        }
      },
      {
        Name: 'Attendance',
        'Sub-pages': [
          {
            Name: 'Driver Attendance',
            Actions: {
              View: false
            }
          },
          {
            Name: 'Employee Attendance',
            Actions: {
              View: false
            }
          }
        ]
      },
      {
        Name: 'Payrolls',
        'Sub-pages': [
          {
            Name: 'Driver Payrolls',
            Actions: {
              View: false,
              Maker: false,
              Checker: false,
              Approver: false,
              Payer: false
            }
          },
          {
            Name: 'Employee Payrolls',
            Actions: {
              View: false,
              Maker: false,
              Checker: false,
              Approver: false,
              Payer: false
            }
          }
        ]
      }
    ]
  },
  'Operations Section': {
    Title: 'Operations',
    Pages: [
      {
        Name: 'PO',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Approver: false
        }
      },
      {
        Name: 'DO',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Approver: false
        }
      },
      {
        Name: 'Permit',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Approver: false
        }
      },
      {
        Name: 'Trips',
        'Sub-pages': [
          {
            Name: 'Running Trips',
            Actions: {
              View: false
            }
          },
          {
            Name: 'Breakdown Trips',
            Actions: {
              View: false
            }
          },
          {
            Name: 'Completed Trips',
            Actions: {
              View: false
            }
          }
        ]
      }
    ]
  },
  'Setting Section': {
    Title: 'Settings',
    Pages: [
      {
        Name: 'Department',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      },
      {
        Name: 'Designation',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      },
      {
        Name: 'Shifts',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      },
      {
        Name: 'Holidays',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      },
      {
        Name: 'Material',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      },
      {
        Name: 'Vehicle Type',
        Actions: {
          View: false,
          Maker: false,
          Edit: false,
          Delete: false,
          Approver: false
        }
      }
    ]
  }
};

// Get icon for section
const getSectionIcon = sectionKey => {
  switch (sectionKey) {
    case 'Dashboard Section':
      return <FeatherIcon icon="grid" size={16} />;
    case 'Payment Section':
      return <FeatherIcon icon="dollar-sign" size={16} />;
    case 'Masters Section':
      return <FeatherIcon icon="database" size={16} />;
    case 'HRMS Section':
      return <FeatherIcon icon="users" size={16} />;
    case 'Operations Section':
      return <FeatherIcon icon="truck" size={16} />;
    case 'Setting Section':
      return <FeatherIcon icon="settings" size={16} />;
    default:
      return <FeatherIcon icon="file" size={16} />;
  }
};

// Get icon for action type
const getActionIcon = action => {
  switch (action) {
    case 'View':
      return <FeatherIcon icon="eye" size={12} />;
    case 'Maker':
      return <FeatherIcon icon="plus" size={12} />;
    case 'Edit':
      return <FeatherIcon icon="edit" size={12} />;
    case 'Delete':
      return <FeatherIcon icon="trash" size={12} />;
    case 'Approver':
      return <FeatherIcon icon="check" size={12} />;
    case 'Checker':
      return <FeatherIcon icon="text" size={12} />;
    case 'Payer':
      return <FeatherIcon icon="dollar-sign" size={12} />;
    case 'Verifier':
      return <FeatherIcon icon="check" size={12} />;
    default:
      return <FeatherIcon icon="eye" size={12} />;
  }
};

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 15px;
  color: ${props => props.color || '#fff'};
  background-color: ${props => props.background || '#f8f9fa'};
  border: 1px solid ${props => props.border || '#e9ecef'};
  margin-left: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  ${props =>
    props.variant === 'primary' &&
    `
    background-color: #7367f0;
    color: white;
    border: none;
    &:hover {
      background-color: #5e50ee;
    }
  `}
  ${props =>
    props.variant === 'danger' &&
    `
    background-color: #ea5455;
    color: white;
    border: none;
    &:hover {
      background-color: #d63031;
    }
  `}
  ${props =>
    props.variant === 'secondary' &&
    `
    background-color: #fff;
    color: #82868b;
    border: 1px solid #d8d6de;
    &:hover {
      background-color: #f8f8f8;
    }
  `}
`;

const nonEditableRoles = ['admin', 'employee', 'superadmin'];

// Main component
const PermissionPage = () => {
  const [modalLoading, modalMessage, setModalLoading] = useModalManage();
  const { showAlert } = useAlert();
  const editRole = useShowModal();
  const [selectedRole, setSelectedRole] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [permissions, setPermissions] = useState(
    JSON.parse(JSON.stringify(permissionStructure))
  );
  const [originalPermissions, setOriginalPermissions] = useState(
    JSON.parse(JSON.stringify(permissionStructure))
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStructure, setFilteredStructure] = useState(
    JSON.parse(JSON.stringify(permissionStructure))
  );
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [isEditable, setIsEditable] = useState(true);

  const { 0: role, 3: refreshRole } = useFetchData(
    apiEndpoints.rolesPermission.roles,
    {
      defaultData: [],
      dataModifier: data => data.filter(r => r.role_name !== 'Admin')
    }
  );

  // Update the fetchPermissionJSON function
  const fetchPermissionJSON = async role_id => {
    setModalLoading(true, 'Fetching permission');
    const response = await fetchData(
      apiEndpoints.rolesPermission.getPermission(role_id)
    );
    if (response.status) {
      const fetchedPermission = response.data.permission_json;
      if (fetchedPermission) {
        const flatData = JSON.parse(fetchedPermission);
        const structureData = convertRawDataToPermissionStructure(
          flatData,
          JSON.parse(JSON.stringify(permissionStructure))
        );
        setPermissions(structureData);
        setOriginalPermissions(JSON.parse(JSON.stringify(structureData)));
      } else {
        const defaultPermissions = JSON.parse(
          JSON.stringify(permissionStructure)
        );
        setPermissions(defaultPermissions);
        setOriginalPermissions(JSON.parse(JSON.stringify(defaultPermissions)));
      }
      setIsEditing(false);
      setHasChanges(false);
    } else {
      showAlert({
        variant: 'danger',
        message: response.message || 'Failed to fetch permissions'
      });
    }
    setModalLoading(false);
  };

  // Add a function to check for changes
  const checkForChanges = useCallback(() => {
    const permissionsStr = JSON.stringify(permissions);
    const originalPermissionsStr = JSON.stringify(originalPermissions);
    const changed = permissionsStr !== originalPermissionsStr;
    setHasChanges(changed);
    return changed;
  }, [permissions, originalPermissions]);

  const formik = useFormik({
    initialValues: {
      role_id: '',
      role_name: '',
      permission: ''
    }
  });

  useEffect(() => {
    if (formik.values.role_name) {
      setIsEditable(
        !nonEditableRoles.includes(formik.values.role_name?.toLocaleLowerCase())
      );
    }
  }, [formik.values.role_name]);

  // Initialize expanded sections
  useEffect(() => {
    // Expand first section by default
    const firstSection = Object.keys(permissionStructure)[0];
    setExpandedSections({ [firstSection]: true });
  }, []);

  // Filter structure based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredStructure(permissions);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = {};

    // Helper function to check if an item matches the search query
    const itemMatches = item => {
      if (item.Name.toLowerCase().includes(query)) return true;

      // Check if any action matches
      const actions = item.Action || item.Actions || {};
      if (
        Object.keys(actions).some(action =>
          action.toLowerCase().includes(query)
        )
      )
        return true;

      // Check sub-pages
      if (item['Sub-pages']) {
        return item['Sub-pages'].some(subItem => itemMatches(subItem));
      }

      return false;
    };

    // Filter sections and their pages
    Object.entries(permissions).forEach(([key, section]) => {
      const matchingPages = section.Pages.filter(page => itemMatches(page));

      if (
        section.Title.toLowerCase().includes(query) ||
        matchingPages.length > 0
      ) {
        filtered[key] = {
          Title: section.Title,
          Pages: matchingPages.length > 0 ? matchingPages : section.Pages
        };
      }
    });

    setFilteredStructure(filtered);

    // Auto-expand sections with matches
    if (Object.keys(filtered).length > 0) {
      const newExpandedSections = { ...expandedSections };
      Object.keys(filtered).forEach(key => {
        newExpandedSections[key] = true;
      });
      setExpandedSections(newExpandedSections);
    }
  }, [searchQuery, permissions]);

  // Toggle section expansion
  const toggleSection = sectionKey => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Get actions from a page item
  const getActions = item => {
    return item.Action || item.Actions || {};
  };

  // Toggle permission for a specific item and action
  const togglePermission = (path, action) => {
    if (!isEditing) return;

    setPermissions(prevPermissions => {
      const newPermissions = JSON.parse(JSON.stringify(prevPermissions));

      // Navigate to the correct item
      let current = newPermissions;
      let item = null;

      for (let i = 0; i < path.length; i++) {
        const segment = path[i];

        if (i === 0) {
          // Section
          current = current[segment];
        } else if (i === 1) {
          // Page
          item = current.Pages.find(p => p.Name === segment);
          current = item;
        } else {
          // Sub-page
          item = current['Sub-pages']?.find(p => p.Name === segment);
          current = item;
        }
      }

      // Get actions object
      const actions = current.Action || current.Actions || {};

      // Define which actions require View permission
      const viewDependentActions = ['Maker', 'Edit', 'Delete'];
      // Define which actions are independent
      const independentActions = ['Payer', 'Checker', 'Approver'];

      // Special handling for View action
      if (action === 'View') {
        // If deselecting View, deselect all dependent actions but keep independent ones
        if (actions[action] === true) {
          Object.keys(actions).forEach(key => {
            if (!independentActions.includes(key)) {
              actions[key] = false;
            }
          });
        } else {
          // If selecting View, just toggle it
          actions[action] = !actions[action];
        }
      } else {
        // For other actions, toggle the action
        actions[action] = !actions[action];

        // If selecting a view-dependent action, ensure View is also selected
        if (
          actions[action] === true &&
          viewDependentActions.includes(action) &&
          actions['View'] === false
        ) {
          actions['View'] = true;
        }
      }

      return newPermissions;
    });
  };

  // Check if permission is granted
  const hasPermission = (path, action) => {
    // Navigate to the correct item
    let current = permissions;
    let item = null;

    for (let i = 0; i < path.length; i++) {
      const segment = path[i];

      if (i === 0) {
        // Section
        current = current[segment];
        if (!current) return false;
      } else if (i === 1) {
        // Page
        item = current.Pages.find(p => p.Name === segment);
        if (!item) return false;
        current = item;
      } else {
        // Sub-page
        item = current['Sub-pages']?.find(p => p.Name === segment);
        if (!item) return false;
        current = item;
      }
    }

    // Check the action
    if (current.Action && current.Action[action] !== undefined) {
      return current.Action[action];
    } else if (current.Actions && current.Actions[action] !== undefined) {
      return current.Actions[action];
    }

    return false;
  };

  // Check if all actions are permitted for a path
  const hasAllPermissions = path => {
    // Navigate to the correct item
    let current = permissions;
    let item = null;

    for (let i = 0; i < path.length; i++) {
      const segment = path[i];

      if (i === 0) {
        // Section
        current = current[segment];
        if (!current) return false;
      } else if (i === 1) {
        // Page
        item = current.Pages.find(p => p.Name === segment);
        if (!item) return false;
        current = item;
      } else {
        // Sub-page
        item = current['Sub-pages']?.find(p => p.Name === segment);
        if (!item) return false;
        current = item;
      }
    }

    // Check all actions on this item
    const actions = current.Action || current.Actions || {};
    const hasAllDirectActions = Object.values(actions).every(
      value => value === true
    );

    // If there are no sub-pages, we can return the result of direct actions
    if (!current['Sub-pages'] || current['Sub-pages'].length === 0) {
      return hasAllDirectActions;
    }

    // If we have sub-pages, check if all sub-pages have all permissions
    const allSubPagesHaveAllPermissions = current['Sub-pages'].every(
      subPage => {
        return checkSubPageHasAllPermissions(subPage);
      }
    );

    // Both this item and all sub-pages must have all permissions to return true
    return hasAllDirectActions && allSubPagesHaveAllPermissions;
  };

  // New helper function to check if a sub-page and all its children have all permissions
  const checkSubPageHasAllPermissions = subPage => {
    // Check direct actions
    const actions = subPage.Action || subPage.Actions || {};
    const hasAllDirectActions = Object.values(actions).every(
      value => value === true
    );

    // If there are no nested sub-pages, return the result of direct actions
    if (!subPage['Sub-pages'] || subPage['Sub-pages'].length === 0) {
      return hasAllDirectActions;
    }

    // Check all nested sub-pages recursively
    const allNestedSubPagesHaveAllPermissions = subPage['Sub-pages'].every(
      nestedSubPage => {
        return checkSubPageHasAllPermissions(nestedSubPage);
      }
    );

    // Both this sub-page and all nested sub-pages must have all permissions
    return hasAllDirectActions && allNestedSubPagesHaveAllPermissions;
  };

  // Check if some actions are permitted for a path
  const hasSomePermissions = path => {
    // Navigate to the correct item
    let current = permissions;
    let item = null;

    for (let i = 0; i < path.length; i++) {
      const segment = path[i];

      if (i === 0) {
        // Section
        current = current[segment];
        if (!current) return false;
      } else if (i === 1) {
        // Page
        item = current.Pages.find(p => p.Name === segment);
        if (!item) return false;
        current = item;
      } else {
        // Sub-page
        item = current['Sub-pages']?.find(p => p.Name === segment);
        if (!item) return false;
        current = item;
      }
    }

    // Check if any action is true
    const actions = current.Action || current.Actions || {};
    const hasSomeDirectActions = Object.values(actions).some(
      value => value === true
    );

    // If direct actions include some true values, we can return true
    if (hasSomeDirectActions) {
      return true;
    }

    // If no direct actions are true but we have sub-pages, check them
    if (current['Sub-pages'] && current['Sub-pages'].length > 0) {
      return current['Sub-pages'].some(subPage => {
        return checkSubPageHasSomePermissions(subPage);
      });
    }

    return false;
  };

  // New helper function to check if a sub-page or any of its children have some permissions
  const checkSubPageHasSomePermissions = subPage => {
    // Check direct actions
    const actions = subPage.Action || subPage.Actions || {};
    const hasSomeDirectActions = Object.values(actions).some(
      value => value === true
    );

    // If direct actions include some true values, we can return true
    if (hasSomeDirectActions) {
      return true;
    }

    // If no direct actions are true but we have nested sub-pages, check them
    if (subPage['Sub-pages'] && subPage['Sub-pages'].length > 0) {
      return subPage['Sub-pages'].some(nestedSubPage => {
        return checkSubPageHasSomePermissions(nestedSubPage);
      });
    }

    return false;
  };

  // Toggle all actions for a specific item
  const toggleAllActions = (path, value) => {
    if (!isEditing) return;
    setPermissions(prevPermissions => {
      const newPermissions = JSON.parse(JSON.stringify(prevPermissions));

      // Navigate to the correct item using path
      let current = newPermissions;
      let item = null;

      for (let i = 0; i < path.length; i++) {
        const segment = path[i];

        if (i === 0) {
          // Section
          current = current[segment];
        } else if (i === 1) {
          // Page
          item = current.Pages.find(p => p.Name === segment);
          current = item;
        } else {
          // Sub-page
          item = current['Sub-pages']?.find(p => p.Name === segment);
          current = item;
        }
      }

      // Set all actions
      if (current.Action) {
        Object.keys(current.Action).forEach(action => {
          current.Action[action] = value;
        });
      } else if (current.Actions) {
        Object.keys(current.Actions).forEach(action => {
          current.Actions[action] = value;
        });
      }

      // If it has sub-pages, toggle them too
      if (current['Sub-pages']) {
        current['Sub-pages'].forEach(subPage => {
          toggleSubPagePermissions(subPage, value);
        });
      }

      return newPermissions;
    });
  };

  // New helper function to toggle permissions on a sub-page and its children
  const toggleSubPagePermissions = (subPage, value) => {
    // Set actions on this sub-page
    if (subPage.Action) {
      Object.keys(subPage.Action).forEach(action => {
        subPage.Action[action] = value;
      });
    } else if (subPage.Actions) {
      Object.keys(subPage.Actions).forEach(action => {
        subPage.Actions[action] = value;
      });
    }

    // Recursively handle nested sub-pages if they exist
    if (subPage['Sub-pages']) {
      subPage['Sub-pages'].forEach(nestedSubPage => {
        toggleSubPagePermissions(nestedSubPage, value);
      });
    }
  };

  // Helper function to toggle all actions in sub-pages recursively
  const toggleSubPagesRecursively = (subPages, value) => {
    subPages.forEach(subPage => {
      if (subPage.Action) {
        Object.keys(subPage.Action).forEach(action => {
          subPage.Action[action] = value;
        });
      } else if (subPage.Actions) {
        Object.keys(subPage.Actions).forEach(action => {
          subPage.Actions[action] = value;
        });
      }

      if (subPage['Sub-pages']) {
        toggleSubPagesRecursively(subPage['Sub-pages'], value);
      }
    });
  };

  // Toggle all permissions in a section
  const toggleSectionPermissions = (sectionKey, value) => {
    if (!isEditing) return;
    setPermissions(prevPermissions => {
      const newPermissions = JSON.parse(JSON.stringify(prevPermissions));
      const section = newPermissions[sectionKey];

      // Toggle all pages in the section
      section.Pages.forEach(page => {
        if (page.Action) {
          Object.keys(page.Action).forEach(action => {
            page.Action[action] = value;
          });
        } else if (page.Actions) {
          Object.keys(page.Actions).forEach(action => {
            page.Actions[action] = value;
          });
        }

        // Toggle sub-pages if any
        if (page['Sub-pages']) {
          toggleSubPagesRecursively(page['Sub-pages'], value);
        }
      });

      return newPermissions;
    });
  };

  // Check if section has any permissions
  const sectionHasPermissions = sectionKey => {
    const section = permissions[sectionKey];
    if (!section) return false;

    // Check if any page has permissions
    return section.Pages.some(page => {
      // Check direct actions
      const actions = page.Action || page.Actions || {};
      if (Object.values(actions).some(value => value === true)) {
        return true;
      }

      // Check sub-pages
      if (page['Sub-pages']) {
        return page['Sub-pages'].some(subPage => {
          const subActions = subPage.Action || subPage.Actions || {};
          return Object.values(subActions).some(value => value === true);
        });
      }

      return false;
    });
  };

  // Check if section has some permissions (for indeterminate state)
  const sectionHasSomePermissions = sectionKey => {
    return sectionHasPermissions(sectionKey);
  };

  // Check if section has all permissions
  const sectionHasAllPermissions = sectionKey => {
    const section = permissions[sectionKey];
    if (!section) return false;

    // Check if all pages have all permissions
    return section.Pages.every(page => {
      // First check direct actions on the page
      const pageActions = page.Action || page.Actions || {};
      const pageHasAllDirectActions = Object.values(pageActions).every(
        value => value === true
      );

      // If any direct action is false, we can immediately return false
      if (!pageHasAllDirectActions) {
        return false;
      }

      // If this page has sub-pages, check if they all have all permissions
      if (page['Sub-pages'] && page['Sub-pages'].length > 0) {
        return page['Sub-pages'].every(subPage => {
          return checkSubPageHasAllPermissions(subPage);
        });
      }

      // If no sub-pages and all direct actions are true, return true
      return true;
    });
  };

  const flattenPermissionObject = obj => {
    let result = {};
    for (const p of obj) {
      if (p['Sub-pages']) {
        result = { ...result, ...flattenPermissionObject(p['Sub-pages']) };
      } else {
        result[p.Name] = p.Actions;
      }
    }
    return result;
  };

  // Save permissions
  const savePermissions = async () => {
    let flat_permissions = {};
    for (const sectionKey in permissions) {
      flat_permissions = {
        ...flat_permissions,
        ...flattenPermissionObject(permissions[sectionKey].Pages)
      };
    }
    setModalLoading(true, 'Updating permissions');
    const request = await postData(
      {
        role_id: selectedRole,
        permission_json: JSON.stringify(flat_permissions)
      },
      apiEndpoints.rolesPermission.updatePermission
    );

    if (request.status) {
      setModalLoading(false);
      showAlert({
        variant: 'success',
        message: `Successfully updated the permissions for ${formik.values.role_name}.`
      });
      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
      setIsEditing(false);
      setHasChanges(false);
      setShowUnsavedModal(false);

      if (pendingRoleChange) {
        handleRoleChange(pendingRoleChange);
        setPendingRoleChange(null);
      }
    } else {
      setModalLoading(false);
      showAlert({
        variant: 'danger',
        message: `Failed to update permissions for ${formik.values.role_name}.`
      });
    }
  };

  // Add function to start editing mode
  const startEditing = () => {
    setIsEditing(true);
  };

  // Add function to discard changes
  const discardChanges = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
    setIsEditing(false);
    setHasChanges(false);
    setShowUnsavedModal(false);

    if (pendingRoleChange) {
      handleRoleChange(pendingRoleChange);
      setPendingRoleChange(null);
    }
  };

  // Handle role change with unsaved changes check
  const handleRoleChange = newRole => {
    if (hasChanges) {
      setPendingRoleChange(newRole);
      setShowUnsavedModal(true);
      return;
    }

    setSelectedRole(newRole.value);
    fetchPermissionJSON(newRole.value);
    formik.setFieldValue('role_id', newRole.value);
    formik.setFieldValue('role_name', newRole.label);
  };

  // Effect to check for changes when permissions are updated
  useEffect(() => {
    if (selectedRole) {
      checkForChanges();
    }
  }, [permissions, checkForChanges, selectedRole]);

  // Render action items recursively
  const renderActionItems = (items, parentPath = [], level = 0) => {
    return items.map((item, index) => {
      const currentPath = [...parentPath, item.Name];
      const hasSubPages = item['Sub-pages'] && item['Sub-pages'].length > 0;
      const pathKey = currentPath.join('.');
      const isExpanded = expandedSections[pathKey] || false;
      const actions = getActions(item);

      // Use our improved permission check functions
      const allChecked = hasAllPermissions(currentPath);
      const someChecked = hasSomePermissions(currentPath) && !allChecked;

      return (
        <React.Fragment key={`${pathKey}-${index}`}>
          <ActionItem level={level}>
            <ActionName>
              {hasSubPages && (
                <span
                  onClick={() => toggleSection(pathKey)}
                  style={{ cursor: 'pointer' }}
                >
                  {isExpanded ? (
                    <FeatherIcon icon="chevron-down" size={16} />
                  ) : (
                    <FeatherIcon icon="chevron-right" size={16} />
                  )}
                </span>
              )}
              <IndentedContent level={level}>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={input => {
                      if (input) {
                        input.indeterminate = someChecked;
                      }
                    }}
                    onChange={() => toggleAllActions(currentPath, !allChecked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  {item.Name}
                </CheckboxLabel>
              </IndentedContent>
            </ActionName>

            <ActionControls>
              {Object.keys(actions).map((action, actionIndex) => (
                <ActionBadge
                  key={`${pathKey}-${action}-${actionIndex}`}
                  isActive={hasPermission(currentPath, action)}
                  onClick={() => togglePermission(currentPath, action)}
                >
                  <ActionIcon>{getActionIcon(action)}</ActionIcon> {action}
                </ActionBadge>
              ))}
            </ActionControls>
          </ActionItem>

          {hasSubPages && isExpanded && (
            <div className="border-bottom border-200">
              {renderActionItems(item['Sub-pages'], currentPath, level + 1)}
            </div>
          )}
        </React.Fragment>
      );
    });
  };

  // const handleRoleEditModal = () => {
  //   const currentRole = role?.find(role => role.role_id === selectedRole);
  //   if (currentRole) {
  //     editRole.handleOpenModal(currentRole);
  //   }
  // };

  return (
    <PageWrapper>
      <PageHeader className="">
        <div className="px-4">
          <Row>
            <Col>
              <PageTitle>
                <FeatherIcon icon="shield" size={20} className="me-2" />{' '}
                {isEditing ? (
                  <>
                    Editing Permissions: {formik.values.role_name}
                    {hasChanges && (
                      <StatusBadge
                        background="#fff2e8"
                        color="#ff8b33"
                        border="#ffd4b1"
                      >
                        <FeatherIcon
                          icon="alert-circle"
                          size={12}
                          className="me-1"
                        />
                        Unsaved Changes
                      </StatusBadge>
                    )}
                  </>
                ) : (
                  <>
                    Permission Management
                    {selectedRole && (
                      <StatusBadge
                        background="#e7f0f9"
                        color="#5e60ce"
                        border="#c4d3e9"
                      >
                        <FeatherIcon icon="eye" size={12} className="me-1" />
                        Viewing
                      </StatusBadge>
                    )}
                  </>
                )}
              </PageTitle>
              <PageDescription>
                {isEditing
                  ? `Configure access permissions for ${formik.values.role_name} role`
                  : 'Configure access permissions for different user roles'}
              </PageDescription>
            </Col>
            <Col xs={12} md="auto">
              <HeaderControls>
                {isEditing ? (
                  <>
                    <Button
                      variant="phoenix-secondary"
                      className="px-3"
                      onClick={discardChanges}
                    >
                      <FeatherIcon icon="x" size={14} /> Discard Changes
                    </Button>
                    <Button
                      variant="phoenix-success"
                      className="px-3"
                      onClick={savePermissions}
                      disabled={!hasChanges}
                    >
                      <FeatherIcon icon="save" size={14} /> Save Changes
                    </Button>
                  </>
                ) : (
                  <>
                    <LabelSelector
                      allowFooter={true}
                      footerBtnFunc={(searchText, handleClickOpen) => (
                        <NewRoleBtn
                          searchText={searchText}
                          handleClickOpen={handleClickOpen}
                          setFunction={data => {
                            setSelectedRole(data.data.role_id);
                            handleRoleChange({
                              value: data.data.role_id,
                              label: data.name
                            });
                          }}
                          handleRefresh={refreshRole}
                        />
                      )}
                      callbackOnChange={handleRoleChange}
                      options={{
                        data: role,
                        valueKey: 'role_id',
                        labelKey: 'role_name'
                      }}
                      formik={formik}
                      controlLabelId={'role_name'}
                      controlId={'role_id'}
                    />
                    {selectedRole && (
                      <div className="flex-shrink-0">
                        <Button
                          variant="primary"
                          className="fw-normal px-3"
                          onClick={startEditing}
                          disabled={!isEditable}
                        >
                          <FeatherIcon icon="edit" size={14} /> Edit Permission
                        </Button>
                        {/* <Button
                          variant="phoenix-secondary"
                          className="fw-medium px-3"
                          onClick={handleRoleEditModal}
                        >
                          <FeatherIcon icon="edit" size={14} /> Edit Role
                        </Button> */}
                      </div>
                    )}
                  </>
                )}
              </HeaderControls>
            </Col>
          </Row>
        </div>
      </PageHeader>
      <Container fluid="md" className="mb-0 px-0">
        <div className="d-flex justify-content-between align-items-center gap-4 mb-3">
          <SearchInput className="mb-0">
            <SearchIcon>
              <FeatherIcon icon="search" size={16} />
            </SearchIcon>
            <Input
              placeholder="Search for pages, actions, or sections..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchInput>
          {/* {selectedRole && !isEditing && (
            <div className=" flex-shrink-0">
              <Button
                variant="primary"
                className="fw-normal px-3"
                onClick={startEditing}
              >
                <FeatherIcon icon="edit" size={14} /> Edit Permission
              </Button>
            </div>
          )} */}
        </div>

        {Object.keys(filteredStructure).length === 0 ? (
          <NoResults>
            <FeatherIcon icon="alert" size={24} className="mb-2" />
            <p>No results found for "{searchQuery}"</p>
            <ActionButton onClick={() => setSearchQuery('')}>
              Clear Search
            </ActionButton>
          </NoResults>
        ) : (
          <>
            {Object.keys(filteredStructure).map(sectionKey => {
              const section = filteredStructure[sectionKey];
              const isExpanded = expandedSections[sectionKey] || false;
              const hasAllPermissions = sectionHasAllPermissions(sectionKey);
              const hasSomePermissions =
                sectionHasSomePermissions(sectionKey) && !hasAllPermissions;

              return (
                <Card key={sectionKey}>
                  <CardHeader isExpanded={isExpanded}>
                    <SectionTitle>
                      <input
                        type="checkbox"
                        checked={hasAllPermissions}
                        ref={input => {
                          if (input) {
                            input.indeterminate = hasSomePermissions;
                          }
                        }}
                        onChange={() =>
                          toggleSectionPermissions(
                            sectionKey,
                            !hasAllPermissions
                          )
                        }
                        style={{ marginRight: '0.5rem' }}
                        disabled={!isEditing}
                      />
                      <SectionIcon>{getSectionIcon(sectionKey)}</SectionIcon>
                      {section.Title}
                      {hasSomePermissions && <Badge>Active</Badge>}
                    </SectionTitle>
                    <div className="d-flex align-items-center gap-2">
                      <ActionButton
                        variant="secondary"
                        onClick={() => toggleSection(sectionKey)}
                        style={{ padding: '0.375rem' }}
                      >
                        {isExpanded ? (
                          <FeatherIcon icon="chevron-down" size={16} />
                        ) : (
                          <FeatherIcon icon="chevron-right" size={16} />
                        )}
                      </ActionButton>
                    </div>
                  </CardHeader>

                  {isExpanded && (
                    <CardBody>
                      {renderActionItems(section.Pages, [sectionKey])}
                    </CardBody>
                  )}
                </Card>
              );
            })}
          </>
        )}
      </Container>
      <UnsavedChangesModal
        show={showUnsavedModal}
        onHide={() => setShowUnsavedModal(false)}
        onSave={savePermissions}
        onDiscard={discardChanges}
      />
      <PageLoader
        fixed={true}
        loading={modalLoading}
        loadingMessage={modalMessage}
      />

      {editRole.isOpen && (
        <EditRoleModal
          show={editRole.isOpen}
          handleClose={editRole.handleCloseModal}
          handleRefresh={refreshRole}
          data={editRole.modalData}
        />
      )}
    </PageWrapper>
  );
};

export default PermissionPage;

function convertRawDataToPermissionStructure(rawData, permissionStructure) {
  // Define the template structure based on the original permissionStructure

  // Helper function to find and update permissions in nested structure
  function updatePermissions(pageName, actions, structure) {
    // Iterate through all sections
    for (const sectionKey in structure) {
      const section = structure[sectionKey];

      // Check each page in the section
      for (const page of section.Pages) {
        // Check sub-pages if exists
        if (page['Sub-pages']) {
          for (const subPage of page['Sub-pages']) {
            if (subPage.Name === pageName) {
              for (const action in actions) {
                if (
                  subPage.Actions &&
                  Object.prototype.hasOwnProperty.call(subPage.Actions, action)
                ) {
                  subPage.Actions[action] = actions[action];
                }
              }
              return true;
            }
          }
        }

        // Direct match for page
        if (page.Name === pageName) {
          for (const action in actions) {
            if (
              page.Actions &&
              Object.prototype.hasOwnProperty.call(page.Actions, action)
            ) {
              page.Actions[action] = actions[action];
            }
          }
          return true;
        }
      }
    }
    return false;
  }

  // Update the template structure with values from raw data
  for (const pageName in rawData) {
    const actions = rawData[pageName];
    updatePermissions(pageName, actions, permissionStructure);
  }

  return permissionStructure;
}

const UnsavedChangesModal = ({ show, onHide, onSave, onDiscard }) => {
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Unsaved Changes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        You have unsaved permission changes. Would you like to save your changes
        or discard them?
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onDiscard}>
          Discard Changes
        </Button>
        <Button variant="primary" onClick={onSave}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
